<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from "vue";

import Btn from "@/components/ui/atoms/Btn.vue";

const scrollContainer = ref<HTMLElement | null>(null);
const isHovering = ref(false);
const hasOverflow = ref(false);
const hasLeftOverflow = ref(false);
const hasRightOverflow = ref(false);
const scrollContent = ref<HTMLElement | null>(null);
const isScrolling = ref(false);

const marginLeft = ref(0);

interface Props {
  tag?: keyof HTMLElementTagNameMap;
  theme?: string;
  behaviour?: "scroll" | "stripes";
}

withDefaults(defineProps<Props>(), {
  tag: "div",
  theme: "off-black",
  behaviour: "scroll",
});

const checkOverflow = () => {
  if (!scrollContainer.value || !scrollContent.value) return;

  const container = scrollContainer.value;
  const content = scrollContent.value;

  const contentVisibleWidth = Math.abs(marginLeft.value) + container.clientWidth;
  const contentOverflowWidth = content.scrollWidth - contentVisibleWidth;

  hasOverflow.value = contentOverflowWidth > 0;
  hasLeftOverflow.value = marginLeft.value < 0;
  hasRightOverflow.value = contentOverflowWidth > 0;
};

const handleMouseEnter = () => {
  isHovering.value = true;
  checkOverflow();
};

const handleMouseLeave = () => {
  isHovering.value = false;
  hasLeftOverflow.value = false;
  hasRightOverflow.value = false;
};

const resetScroll = () => {
  marginLeft.value = 0;
  if (scrollContent.value) {
    scrollContent.value.style.marginLeft = `${marginLeft.value}px`;
  }
  checkOverflow();
};

const scrollRight = () => {
  if (scrollContent.value && !isScrolling.value) {
    isScrolling.value = true;
    const container = scrollContainer.value;
    const content = scrollContent.value;

    if (!container || !content) return;

    const item = content.firstElementChild as HTMLElement;
    if (item) {
      const itemWidth = item.offsetWidth;
      const gap = parseFloat(getComputedStyle(content).gap) || 0;
      const totalItemWidth = itemWidth + gap + content.children.length;

      // Calculate the total scroll width needed
      const totalScrollWidth = content.scrollWidth - container.clientWidth;

      // Calculate the number of clicks needed to cover the entire scroll width
      // const maxScrollClicks = Math.ceil(totalScrollWidth / totalItemWidth);

      // Calculate the scroll amount per click
      // const scrollPerClick = totalScrollWidth / maxScrollClicks;
      const scrollPerClick = totalItemWidth;

      // Perform the scroll
      if (
        Math.abs(marginLeft.value) + container.clientWidth + scrollPerClick <=
        content.scrollWidth
      ) {
        marginLeft.value -= scrollPerClick;
        content.style.marginLeft = `${marginLeft.value}px`;
        checkOverflow();
      } else {
        // Align to the end of the container
        marginLeft.value = -totalScrollWidth;
        content.style.marginLeft = `${marginLeft.value}px`;
        hasOverflow.value = false;
      }

      setTimeout(() => {
        isScrolling.value = false;
      }, 350);
    }
  }
};

const scrollLeft = () => {
  if (scrollContent.value && !isScrolling.value) {
    isScrolling.value = true;
    const container = scrollContainer.value;
    const content = scrollContent.value;

    if (!container || !content) return;

    const item = content.firstElementChild as HTMLElement;
    if (item) {
      const itemWidth = item.offsetWidth;
      const gap = parseFloat(getComputedStyle(content).gap) || 0;
      const totalItemWidth = itemWidth + gap + content.children.length * 2;

      // Calculate the total scroll width necessary
      // const totalScrollWidth = content.scrollWidth - container.clientWidth;

      // Calculate the number of clicks needed to cover the entire scroll width
      // const maxScrollClicks = Math.ceil(totalScrollWidth / totalItemWidth);

      const scrollPerClick = totalItemWidth;
      // Calculate the scroll amount per click
      //const scrollPerClick = totalScrollWidth / maxScrollClicks;

      // Perform the scroll
      if (Math.abs(marginLeft.value) - scrollPerClick >= 0) {
        marginLeft.value += scrollPerClick;
        content.style.marginLeft = `${marginLeft.value}px`;
        checkOverflow();
      } else {
        // Align to the start of the container
        marginLeft.value = 0;
        content.style.marginLeft = `${marginLeft.value}px`;
      }

      setTimeout(() => {
        isScrolling.value = false;
      }, 350);
    }
  }
};

onMounted(() => {
  nextTick(() => {
    checkOverflow();
  });
});

watch(marginLeft, () => {
  checkOverflow();
});

defineExpose({
  resetScroll,
});
</script>

<template>
  <div
    :class="`block-container ${theme} ${behaviour}`"
    ref="scrollContainer"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      :class="`block-container ${theme} ${behaviour} scroll-content`"
      ref="scrollContent"
    >
      <slot />
    </div>
    <Btn
      v-if="behaviour === 'scroll'"
      theme="clear-bold"
      size="huge"
      action="prev-clear"
      @click="scrollLeft"
      :class="['scroll-prev-btn', { hidden: !hasLeftOverflow }]"
    />
    <Btn
      v-if="behaviour === 'scroll'"
      theme="clear-bold"
      size="huge"
      action="next-clear"
      @click="scrollRight"
      :class="['scroll-next-btn', { hidden: !hasOverflow }]"
    />
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/settings/vars";

.scroll {
  position: relative;
}

.scroll-content {
  transition: margin-left 0.5s ease;
}

.scroll-next-btn,
.scroll-prev-btn {
  position: absolute;
  border: 1px solid vars.$storm;
  top: 50%;
  right: 0;
  left: auto;
  transform: translateY(-50%) scale(0.7);
  transition:
    opacity 0.2s,
    transform 0.4s,
    visibility 0s 0.2s;
  opacity: 0;
  visibility: hidden;
}

@media (max-width: 1024px) {
  .scroll-next-btn,
  .scroll-prev-btn {
    display: none;
  }
}

.scroll-prev-btn {
  left: 0;
}

.scroll:hover .scroll-next-btn.hidden,
.scroll:hover .scroll-prev-btn.hidden {
  transform: translateY(-50%) scale(0.7);
  transition:
    opacity 0.2s,
    transform 0.4s,
    visibility 0s 0.2s;
  opacity: 0;
  visibility: hidden;
}

.scroll:hover .scroll-next-btn,
.scroll:hover .scroll-prev-btn {
  opacity: 1;
  transform: translateY(-50%) scale(1);
  visibility: visible;
  transition-delay: 0s;
}
</style>
